//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { RefObject, useState } from 'react';
import { Box, Link, Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps, TextField, TextFieldProps } from '@mui/material';
import styled from '@emotion/styled';

export type AutocompleteProps = Partial<MuiAutocompleteProps<any, any, any, any, any>> & {
  options: any[];
  value: any;
  label?: string;
  helperText?: string | null;
  error?: boolean | null;
  placeholder?: string;
  inputProps?: any;
  required?: boolean;
  innerRef?: RefObject<any>;
  hiddenLabel?: boolean;
  variant?: TextFieldProps['variant'];
  onEscape?: () => void;
};

const Autocomplete = ({
  options = [],
  value = null,
  label = '',
  helperText = null,
  error = null,
  placeholder,
  inputProps,
  required = false,
  innerRef,
  hiddenLabel = false,
  variant = 'filled',
  onEscape,
  ...props
}: AutocompleteProps) => {
  const [open, setOpen] = useState(false);

  const onKeyDown = (evt: any) => {
    if (evt.code === 'Escape' && onEscape && !open) {
      onEscape();
    } else if (evt.code === 'Escape' && open) {
      setOpen(false);
    }
  };

  return (
    <StyledAutocomplete
      data-testid="autocomplete"
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      options={options}
      value={value}
      autoHighlight
      openOnFocus
      disableClearable
      renderOption={(roProps, option: any) => (
        <React.Fragment key={(roProps as any).key || option}>
          {!option.isMoreOption && (
            <Box component="li" {...roProps}>
              {props.getOptionLabel ? props.getOptionLabel(option) : option.label ?? option}
            </Box>
          )}
          {option.isMoreOption && (
            <Box component="li" {...roProps}>
              <MoreOptionsLink>{props.getOptionLabel ? props.getOptionLabel(option) : option.label ?? option}</MoreOptionsLink>
            </Box>
          )}
        </React.Fragment>
      )}
      {...props}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          label={label}
          helperText={helperText}
          error={error}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          required={required}
          ref={innerRef}
          hiddenLabel={hiddenLabel}
          onClick={(e) => e.stopPropagation()}
          {...inputProps}
        />
      )}
      small={+(props.size === 'small')}
      ListboxProps={{
        onClick: (e) => e.stopPropagation()
      }}
    />
  );
};

export default Autocomplete;

const MoreOptionsLink = styled(Link)`
  texttransform: uppercase;
  fontsize: 0.8rem;
`;

const StyledAutocomplete = styled(MuiAutocomplete)<{ small: number }>`
  .MuiAutocompleteListbox-root {
    padding-left: 0px;
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    max-height: 30vh;
    overflow: auto;
  }

  ${({ small }) =>
    small &&
    `
  font-size: 0.95rem;
  `}
`;
